/**
 * The engine choice as a hook: which engine is active, whether the user may
 * change it, and how to change it safely.
 *
 * Both surfaces that offer the choice — the onboarding bubble and the Settings
 * panel — need the same three things, and switching engines has two easy
 * mistakes in it that are worth writing down once rather than twice.
 *
 * Lives outside `onboarding/` because Settings uses it too.
 */
import { useCallback } from "react";

import {
  cancelAllVoiceEngines,
  currentVoiceEngine,
  isVoiceEngineSelectable,
  primeDenkerAgentVoiceAudio,
} from "./denker-agent-voice";
import type { VoiceEngineChoice } from "./voice-engine-options";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";

export function useVoiceEngineChoice(options?: {
  onChoose?: (engine: VoiceEngineChoice) => void;
}) {
  // Subscribed, not read once: `currentVoiceEngine()` below reads a module
  // variable, so without this subscription the component would never re-render
  // when the choice changes.
  const chosen = useTtsSettingsStore((s) => s.voiceEngine);
  const setVoiceEngine = useTtsSettingsStore((s) => s.setVoiceEngine);
  const onChoose = options?.onChoose;

  const choose = useCallback(
    (engine: VoiceEngineChoice) => {
      // Stop BOTH engines, not just the one being left. Cancelling only the
      // newly-selected engine would leave the previous one still speaking over
      // the sample the user is about to hear.
      void cancelAllVoiceEngines();
      setVoiceEngine(engine);
      // Only Kokoro has a worker to spin up; the Windows engine is resident in
      // the OS and answers on first call.
      if (engine === "kokoro") primeDenkerAgentVoiceAudio();
      onChoose?.(engine);
    },
    [onChoose, setVoiceEngine],
  );

  return {
    /** The engine in effect, honouring the user's choice where there is one. */
    engine: currentVoiceEngine(),
    /** Whether to offer the choice at all — see `isVoiceEngineSelectable`. */
    isSelectable: isVoiceEngineSelectable(),
    /** The persisted choice, or null while following the machine default.
     *  Useful as an effect dependency. */
    chosen,
    choose,
  };
}
